/* REACT PACKAGE IMPORTS */
import { useEffect, useState, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, Navigate } from "react-router-dom";

/* ENTRA IMPORTS */
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";

/* INTERNAL IMPORTS */
// Pages
import Candidate from "./Pages/Candidate";
import LoginEntra from "./Pages/LoginEntra";
import NoMatch from "./Pages/NoMatch";
import FallbackComponent from "./Pages/FallbackComponent";
import Unauthorized from "./Pages/Unauthorized";
import CandidateLoginEntra from './Pages/CandidateLoginEntra'

// Components
import ErrorBoundary from "./Components/ErrorBoundary";
import SimpleSnackbar from "./Components/SnackBar";
import RequireAuth from "./Components/RequireAuth";

// Styling
import "./styles/App.css";
import "./styles/theme.css";
import "./styles/Form.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { ThemeContext } from "./Theme/ThemeContext";

// Images
import FIFA_LOGO from "./assets/FIFA-Logo.png";
import LayoutEntra from "./Components/LayoutEntra";

/**
 * Most applications will need to conditionally render certain components based on whether a user is signed in or not.
 * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will
 * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const MainContent = () => {
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */

  const context = useContext(ThemeContext);
  const [logoID, setLogoId] = useState(FIFA_LOGO);
  const { userInfo } = useSelector((store) => store.auth);
  const mounted = useRef(null);

  useEffect(() => {
    mounted.current = true;
    if (mounted.current) {
      if (userInfo) {
        const { theme } = userInfo;
        var body = document.querySelector("body");
        if (theme.length === 0) {
          body.style.setProperty("--bkg-color", "#ffffff");
          body.style.setProperty("--bkgsecondary", "#ffffff");
          body.style.setProperty("--text-color", "#000000");
          context.toggleColorMode("light");
          setLogoId(FIFA_LOGO);
          return;
        } else {
          const themeObj = JSON.parse(theme);
          const primarycolor = themeObj.primarycolor;
          const secondarycolor = themeObj.secondarycolor;
          const fontcolor = themeObj.fontcolor;
          const logo = themeObj.logo;
          const mode = themeObj.mode;
          body.style.setProperty("--bkg-color", primarycolor);
          body.style.setProperty("--bkgsecondary", secondarycolor);
          body.style.setProperty("--text-color", fontcolor);
          if (mode !== null) context.toggleColorMode(mode);
          else context.toggleColorMode("light");
          if (logo !== null) setLogoId(logo);
          else setLogoId(FIFA_LOGO);
          return;
        }
      }
    }
  }, [userInfo, logoID, context]);

  return (
    <div className="">
      <AuthenticatedTemplate>
        <ErrorBoundary>
          {/* show errors */}
          <SimpleSnackbar/>
          <Routes>
            {/* public routes */}
            <Route
              path="login"
              element={<CandidateLoginEntra logo={logoID}/>}
            />
            <Route
              path="login?"
              element={<CandidateLoginEntra logo={logoID}/>}
            />

            {/* candidate routes */}
            <Route
              path="/"
              element={
                <RequireAuth />
              }
            >
              <Route
                path="/"
                element={<Navigate to="/candidate/assessments"/>}
              />
              <Route
                path="/candidate"
                element={<Navigate to="/candidate/assessments"/>}
              />
              <Route
                path="candidate"
                element={
                  <LayoutEntra
                    isEmbedded={false}
                    logo={logoID}
                    tabs={["Home"]}
                  />
                }
              >
                <Route path="*" element={<Candidate/>}/>
                <Route path="/candidate/error" element={<NoMatch/>}/>
              </Route>
            </Route>

            {/* catch all */}
            <Route path="unauthorized" element={<Unauthorized/>}/>
            {/* catch all */}
            <Route path="*" element={<NoMatch/>}/>
          </Routes>
        </ErrorBoundary>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Routes>
          <Route path="/" element={<LoginEntra logo={logoID}/>}/>
        </Routes>
      </UnauthenticatedTemplate>
    </div>
  );
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const MSALApp = ({ instance }) => {
  return (
    <MsalProvider instance={instance}>
      <MainContent />
    </MsalProvider>
  );
};

export default MSALApp;
